import { Link } from "react-router-dom";
import { MapPin, Navigation } from "lucide-react";
import { fieldState, getCoords, getLinks, useSite } from "@/lib/content";
import { useSeo } from "@/lib/seo";
import { t } from "@/lib/i18n";
import PageIntro from "@/components/PageIntro";
import Reveal from "@/components/Reveal";
import VisitCard, { VISIT_ICONS } from "@/components/VisitCard";
import { Pending } from "@/components/Pending";
import { path } from "@/lib/routes";
import TicketCta from "@/components/booking/TicketCta";

function MapBlock({ coords, directions, isPreview }) {
  if (!coords && !directions && !isPreview) return null;
  return (
    <section className="safe-x max-w-7xl mx-auto pb-24 lg:pb-32" data-testid="visit-map-section">
      <Reveal className="rounded-3xl overflow-hidden border border-line bg-white">
        {coords ? (
          <iframe
            title={t("ziyaret.mapLabel")}
            src={`https://www.google.com/maps?q=${coords.lat},${coords.lng}&z=15&output=embed`}
            className="w-full h-[360px] sm:h-[440px] border-0"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            data-testid="visit-map-embed"
          />
        ) : (
          <div className="h-[260px] flex flex-col items-center justify-center gap-3 bg-cream-2" data-testid="visit-map-placeholder">
            <MapPin className="h-8 w-8 text-honey-ink" aria-hidden="true" />
            <p className="font-serif text-2xl text-forest">{t("ziyaret.mapTitle")}</p>
            <Pending testId="visit-map-pending" />
          </div>
        )}
        {directions && (
          <div className="p-5 flex justify-end">
            <a href={directions.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 min-h-[52px] px-7 rounded-full bg-forest text-cream font-semibold hover:bg-forest-dark transition-colors" data-testid="visit-directions-button">
              <Navigation className="h-4 w-4" aria-hidden="true" /> {t("ziyaret.directions")}
            </a>
          </div>
        )}
      </Reveal>
    </section>
  );
}

export default function Ziyaret() {
  useSeo("ziyaret");
  const { content, isPreview } = useSite();
  const fields = Object.keys(VISIT_ICONS).map((k) => [k, fieldState(content?.visit?.[k], isPreview)]).filter(([, s]) => s.show);
  const coords = getCoords(content, isPreview);
  const { directions } = getLinks(content, isPreview);
  const empty = !fields.length && !coords && !directions;

  return (
    <div data-testid="ziyaret-page">
      <PageIntro eyebrow={t("ziyaret.eyebrow")} title={t("ziyaret.title")} text={t("ziyaret.text")} testId="ziyaret-intro" />
      {fields.length > 0 && (
        <section className="safe-x max-w-7xl mx-auto pb-16" data-testid="visit-cards">
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {fields.map(([k, s], i) => <Reveal key={k} delay={(i % 4) * 70}><VisitCard fieldKey={k} state={s} headingLevel={2} /></Reveal>)}
          </div>
        </section>
      )}
      <TicketCta testId="visit-ticket-cta" className="pb-16" />
      <MapBlock coords={coords} directions={directions} isPreview={isPreview} />
      {empty && (
        <div className="safe-x max-w-7xl mx-auto pb-24">
          <Link to={path("kesfet")} className="inline-flex items-center min-h-[52px] px-7 rounded-full bg-forest text-cream font-semibold" data-testid="ziyaret-kesfet-link">{t("ziyaret.backLink")}</Link>
        </div>
      )}
    </div>
  );
}
